<template>
    <div v-if="theme" class="rrlb-calendar transition-all overflow-hidden" :class="[classRoundness, {
        'border': !theme.borderless,
        'border-neutral-700' :theme.darkMode
    }]" :style="{
        '--vc-rounded-full': calendarRoundness
    }">
        <DatePicker :attributes="attributes" :isDark="theme.darkMode" :color="theme.color"
                    :rows="theme.rows" :borderless="true" is-required :locale="locale" expanded
                    :transparent="theme.borderless" :columns="multiselect ? 2 : 1" :is-range="multiselect"
                    :min-date="dateTimeRestrictions.min" :max-date="dateTimeRestrictions.max"
                    :disabled-dates="dateTimeRestrictions.disabled" mode="date" v-model="selectedDate"
                    :view="settings.view" :first-day-of-week="settings.firstDayOfWeek"
                    :title-position="settings.titlePosition" :transition="settings.transition"
                    :show-weeknumbers="settings.showWeekNumbers"
        >
        </DatePicker>
    </div>

    <div class="flex justify-center">
        <div class="px-3 py-2 mt-3 flex flex-col gap-1 border-neutral-100 bg-neutral-300/15 text-xs opacity-80 relative" :class="[classRoundness, {
        'border': !theme.borderless,
        'border-neutral-700' :theme.darkMode
    }]">
            <div class="flex gap-2 items-center">
                <div class="w-5 h-5 flex justify-center items-center opacity-40 line-through">{{today}}</div>
                <p> nedostupné</p>
            </div>
            <div class="flex gap-2 items-center">
                <div class="w-5 h-5 border-2 flex justify-center items-center" :class="`border-${theme.color}-500`" :style="{ borderRadius: calendarRoundness }">{{today}}</div>
                <p> dostupné</p>
            </div>
            <div class="flex gap-2 items-center">
                <div class="w-5 h-5 flex justify-center items-center">{{today}}</div>
                <p> volný termín</p>
            </div>
        </div>
    </div>
</template>
<script setup>
import {DatePicker} from 'v-calendar';
import InlineMessage from 'primevue/inlinemessage';
import RadioButton from 'primevue/radiobutton';
import {defineProps, computed, watch, ref} from 'vue';
import { DateTime } from 'luxon';
import 'v-calendar/style.css';
const props = defineProps({
    settings: Object,
    dateTimeRestrictions: Object,
    events: Array,
    theme: Object,
    multiselect: Boolean,
    locale: String,
    timeslots: Array
});
const today = new Date().getDate();

const classRoundness = computed(() => {
    switch (props.theme.roundness) {
        case 0:
            return 'rounded-0';
        case 1:
            return 'rounded-xl';
            case 2:
                return 'rounded-2xl';
                case 3:
                    return 'rounded-3xl';
                    default:
                        return 'rounded-full';
    }
})

const calendarRoundness = computed(() => {
    switch (props.theme.roundness) {
        case 0:
            return '0px';
        case 1:
            return '5px';
        case 2:
            return '10px';
        case 3:
        default:
            return '999px';
    }
})

const selectedDate = defineModel();

const computedDates = computed(() => {
    return props.events.map((value) => {
        const startDate = DateTime.fromJSDate(new Date(value.startDate));
        const endDate = DateTime.fromJSDate(new Date(value.endDate));
        const eventDay = startDate.toFormat('ccc').toLowerCase();

        const dayEpoch = props.timeslots?.filter(el => {
            const timeslotStart = DateTime.fromISO(el.valid_from);
            const timeslotEnd = DateTime.fromISO(el.valid_to);

            return startDate < timeslotEnd
                && endDate > timeslotStart
                && el.name === eventDay;
        }) ?? [];

        const generatedSlots = [];

        dayEpoch.forEach(day => {
            day.time_slots.forEach(slot => {

                const dayDate = startDate.toFormat('yyyy-MM-dd');

                const startBase = DateTime.fromFormat(
                    `${dayDate} ${slot.time_start}`,
                    'yyyy-MM-dd HH:mm:ss'
                );

                const endBase = DateTime.fromFormat(
                    `${dayDate} ${slot.time_end}`,
                    'yyyy-MM-dd HH:mm:ss'
                );

                if (slot.blocking === 1) {
                    generatedSlots.push({
                        start: startBase,
                        end: endBase,
                        blocking: true
                    });
                    return;
                }

                const interval = Number(slot.interval) || 30;
                const pause = Number(slot.pause) || 0;

                let cursor = startBase;

                while (cursor.plus({ minutes: interval + pause }) <= endBase) {
                    const slotStart = cursor;
                    const slotEnd = cursor.plus({ minutes: interval });

                    generatedSlots.push({
                        start: slotStart.toJSDate(),
                        end: slotEnd.toJSDate(),
                        blocking: false
                    });

                    cursor = cursor.plus({ minutes: interval + pause });
                }
            });
        });

        const blockingEvents = props.events.map(ev => ({
            start: DateTime.fromJSDate(new Date(ev.startDate)),
            end: DateTime.fromJSDate(new Date(ev.endDate))
        }));

        const isFullyBookedOut = generatedSlots.length > 0 && generatedSlots.every(slot => {
            const slotStart = DateTime.fromJSDate(slot.start instanceof Date ? slot.start : slot.start.toJSDate());
            const slotEnd = DateTime.fromJSDate(slot.end instanceof Date ? slot.end : slot.end.toJSDate());

            // slot je blocked, pokud ho překrývá alespoň jeden event dne value
            const slotBlocked = props.events.some(ev => {
                const evStart = DateTime.fromJSDate(new Date(ev.startDate));
                const evEnd = DateTime.fromJSDate(new Date(ev.endDate));

                const dayStart = startDate.startOf('day');
                const dayEnd = startDate.endOf('day');

                // jen eventy, které se překrývají s dnem toho value
                if (evEnd < dayStart || evStart > dayEnd) return false;

                // slot je pokrytý, pokud se s eventem jakkoliv překrývá
                return slotStart < evEnd && slotEnd > evStart;
            });

            return slotBlocked;
        });
        const dayStart = DateTime.fromJSDate(new Date(value.startDate)).startOf('day');
        const dayEnd = DateTime.fromJSDate(new Date(value.startDate)).endOf('day');

        const isAnySlotBooked = generatedSlots.some(slot => {
            const slotStart = DateTime.fromJSDate(slot.start instanceof Date ? slot.start : slot.start.toJSDate());
            const slotEnd = DateTime.fromJSDate(slot.end instanceof Date ? slot.end : slot.end.toJSDate());

            // kontrolujeme jen eventy, které spadají do dne
            return props.events.some(ev => {
                const evStart = DateTime.fromJSDate(new Date(ev.startDate));
                const evEnd = DateTime.fromJSDate(new Date(ev.endDate));

                // jen eventy, které se překrývají s dnem toho value
                if (evEnd < dayStart || evStart > dayEnd) return false;

                // slot je pokrytý, pokud se s eventem jakkoliv překrývá
                return slotStart < evEnd && slotEnd > evStart;
            });
        });
        const hasFullDay = props.events.some(ev => {
            const evStart = DateTime.fromJSDate(new Date(ev.startDate)).startOf('day');
            const evEnd = DateTime.fromJSDate(new Date(ev.endDate)).endOf('day');
            const dayStart = startDate.startOf('day');
            const dayEnd = startDate.endOf('day');

            return ev.wholeDay && evStart <= dayEnd && evEnd >= dayStart;
        });

        if (value.wholeDay || isFullyBookedOut) {
            return {
                content: {
                    class: 'day-disabled'
                },
                dates: [
                    {
                        start: new Date(value.startDate),
                        end: new Date(value.endDate)
                    }
                ],
                popover: {
                    label: value.showNames ? value.title : null
                }
            }
        }

        if (isAnySlotBooked && !hasFullDay) {
            return {
                highlight: {
                    color: props.theme.color,
                    fillMode: 'outline'
                },
                color: 'black',
                dates: [
                    {
                        start: new Date(value.startDate),
                        end: new Date(value.endDate)
                    }
                ]
            }
        }
        return {
            color: 'black',
            dates: [
                {
                    start: new Date(value.startDate),
                    end: new Date(value.endDate)
                }
            ]
        }
    })
});


const attributes = computed(() => [
    {
        key: 'today',
        dates: new Date(),
        order: 0,
        content: {
            style: {
                fontWeight: 'bold',
            }
        }
    },
    ...computedDates.value
]);


</script>
<style lang="scss" scoped>
.rrlb-calendar {
    display: flex;
    gap: 24px;
    flex-direction: row;

    .rrlbc-timeslots {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .rrlbct-timeslot {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 6px;

            .rrlbctt-time {
                font-weight: bold;
                color: var(--primary-color);

                &.disabled {
                    opacity: 0.5;
                }
            }

            .rrlbctt-disabled {
                color: rgb(184, 50, 50);
            }
        }
    }
}
</style>
